import Card from "common/Boxes/Card";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import Divider from "antd/es/divider";
import React, { useContext } from "react";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import Dropdown from "common/Dropdown/Dropdown";
import styled, { useTheme } from "styled-components";
import { formatDate } from "@shared/utils/date";
import { getDisplayedTimeframe } from "@shared/features/bots/utils";
import { BacktestingRunType, BotType, LanguageNameSpaces } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import { openMessage } from "utils/misc";
import PnlComponent from "features/bots/BotForm/Backtesting/common/PnlComponent";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import BacktestStatusTag from "features/bots/BotForm/Backtesting/common/BacktestStatusTag";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import useBacktestingActions from "features/bots/BotForm/Backtesting/common/useBacktestingActions";
import useBacktestingCard from "@shared/features/bots/BotForm/Backtesting/hooks.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import NewLookBacktestCard from "features/bots/BotForm/Backtesting/NewLook/NewLookBacktestCard";

interface BacktestingCardProps {
  data?: BacktestingRunType;
  isSelected?: boolean;
  isSeeAllPage?: boolean;
  botType: BotType;
  setBacktestingToDelete: (value: BacktestingRunType) => void;
}

const BacktestingCard = (props: BacktestingCardProps) => {
  const { isNewLook } = useTheme();
  if (isNewLook) return <NewLookBacktestCard {...props} />;
  return <ClassicBacktestingCard {...props} />;
};

export default React.memo(BacktestingCard);

const ClassicBacktestingCard = ({
  data,
  setBacktestingToDelete,
  isSelected,
  isSeeAllPage,
  botType,
}: BacktestingCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isDrawer } = useContext(DrawerContext) || {};

  const openDeleteModal = () => {
    data && setBacktestingToDelete(data);
  };

  const { menuItems, openExtraDetailsDrawer } = useBacktestingActions({
    data,
    openDeleteModal,
    openMessage,
    isSelected,
    isBotForm: !isSeeAllPage,
    botType,
  });
  const { borderColor } = useBacktestingCard({ isSelected });

  const actionsDropdown = (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        title={t("backtesting.actions.backtest_actions")}
        menu={{ items: menuItems }}
        trigger={["click"]}
      >
        <StyledEllipsisOutlined />
      </Dropdown>
    </div>
  );

  const isSignalsBot = botType === BotType.SIGNAL;
  let iconProps = isSignalsBot
    ? { iconLink: data?.sub_group_image }
    : {
        iconType: ICON_TYPES.COIN,
        coinOrExchangeName: data?.symbol,
      };

  return (
    <StyledCard
      onClick={() => openExtraDetailsDrawer()}
      $color={borderColor}
      $isSeeAllPage={isSeeAllPage}
      $isDrawer={isDrawer}
    >
      <Flex $alignItems="flex-start">
        <Flex style={{ overflow: "hidden" }}>
          {isSeeAllPage && (
            <IconWithFallback
              pxIconSize={50}
              iconStyle={{ borderRadius: 6, marginRight: 16 }}
              {...iconProps}
            />
          )}
          <div style={{ overflow: "hidden" }}>
            <Name $ellipsis={true}>{data?.name}</Name>
            {isSeeAllPage && (
              <Text type="secondary" $ellipsis={true}>
                {isSignalsBot ? data?.sub_group_name : data?.symbol}
              </Text>
            )}
            <CreationDate>
              {t("backtesting.labels.created")}:{" "}
              {formatDate(data?.created_at, true, true)}
            </CreationDate>
          </div>
        </Flex>
        {actionsDropdown}
      </Flex>
      <StyledDivider />
      <InfoWrapper>
        {isSeeAllPage && (
          <Flex style={{ overflow: "hidden" }}>
            <Text type="secondary">{t("labels.account")}</Text>
            <Flex style={{ marginLeft: 8, overflow: "hidden" }}>
              <IconWithFallback
                iconType={ICON_TYPES.EXCHANGE}
                coinOrExchangeName={data?.exchange}
                iconStyle={{ marginRight: 4 }}
              />
              <Text $ellipsis={true}>{data?.client_name}</Text>
            </Flex>
          </Flex>
        )}
        <Flex>
          <Text type="secondary">{t("labels.status")}</Text>
          <BacktestStatusTag backtestingRun={data} />
        </Flex>
        <Flex>
          <Text type="secondary">{t("backtesting.labels.timeframe")}</Text>
          <Title level={4}>
            {getDisplayedTimeframe(data?.backtesting_time_range)}
          </Title>
        </Flex>
        <Flex>
          <Text type="secondary">
            {t(BacktestingGeneralStatistics.NET_PNL)}
          </Text>
          <PnlComponent
            pnlType={BacktestingGeneralStatistics.NET_PNL}
            backtestingRun={data}
          />
        </Flex>
      </InfoWrapper>
    </StyledCard>
  );
};

const StyledCard = styled(Card)<{
  $color?: string;
  $isSeeAllPage?: boolean;
  $isDrawer?: boolean;
}>`
  cursor: pointer;
  ${(p) =>
    !p.$isSeeAllPage &&
    !p.$isDrawer &&
    `
    background: ${p.theme.colors.mediumBgColor};
  `};
  ${(p) =>
    p.$color &&
    `
    border: 1px solid ${p.theme.colors[p.$color]};
    box-shadow: 0px 6px 24px 0px ${hexToRGB(p.theme.colors[p.$color], 0.12)};
  `}
`;
const CreationDate = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const StyledDivider = styled(Divider)`
  margin: 12px 0;
`;
const InfoWrapper = styled.div`
  display: grid;
  row-gap: 12px;
`;
const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  cursor: pointer;
  font-size: 24px;
  margin-left: 12px;
  color: ${(p) => p.theme.colors.basic};
`;
const Name = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
